import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import type { Tables, TablesInsert, TablesUpdate } from '@/lib/supabase/supabase-types';

type WorkExperience = Tables<'work_experience'>;

interface WorkExperienceFormProps {
  experience?: WorkExperience;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<TablesInsert<'work_experience'>, 'freelancer_profile_id'>) => Promise<void>;
  onUpdate?: (data: { id: string } & TablesUpdate<'work_experience'>) => Promise<void>;
  isLoading?: boolean;
}

export function WorkExperienceForm({
  experience,
  isOpen,
  onClose,
  onSubmit,
  onUpdate,
  isLoading = false,
}: WorkExperienceFormProps) {
  const [jobTitle, setJobTitle] = useState('');
  const [company, setCompany] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isCurrent, setIsCurrent] = useState(false);
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');

  const isEditing = !!experience;

  // Initialize form with experience data
  useEffect(() => {
    if (experience) {
      setJobTitle(experience.job_title);
      setCompany(experience.company);
      setStartDate(experience.start_date);
      setEndDate(experience.end_date || '');
      setIsCurrent(!experience.end_date);
      setLocation(experience.location || '');
      setDescription(experience.description || '');
    } else {
      // Reset form for new experience
      setJobTitle('');
      setCompany('');
      setStartDate('');
      setEndDate('');
      setIsCurrent(false);
      setLocation('');
      setDescription('');
    }
  }, [experience, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!jobTitle.trim()) {
      toast.error('Job title is required');
      return;
    }

    if (!company.trim()) {
      toast.error('Company name is required');
      return;
    }

    if (!startDate) {
      toast.error('Start date is required');
      return;
    }

    // Validate end date if not current
    if (!isCurrent && endDate && new Date(endDate) < new Date(startDate)) {
      toast.error('End date must be after start date');
      return;
    }

    try {
      const experienceData = {
        job_title: jobTitle.trim(),
        company: company.trim(),
        start_date: startDate,
        end_date: isCurrent ? null : (endDate || null),
        location: location.trim() || null,
        description: description.trim() || null,
      };

      if (isEditing && onUpdate && experience) {
        await onUpdate({
          id: experience.id,
          ...experienceData,
        });
      } else {
        await onSubmit(experienceData);
      }
      onClose();
    } catch (error) {
      // Error handling is done in the hook
      console.error('Error saving work experience:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-[24px]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-normal text-slate-900">
            {isEditing ? 'Edit Work Experience' : 'Add Work Experience'}
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            {isEditing ? 'Update your work experience details' : 'Add a new work experience entry'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Job Title */}
          <div className="space-y-2">
            <Label htmlFor="jobTitle" className="text-sm font-semibold text-slate-700">Job Title *</Label>
            <Input
              id="jobTitle"
              placeholder="Senior ML Engineer"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              required
              maxLength={100}
              className="rounded-xl border-2 border-slate-200 focus:border-[#4669ed] focus:ring-0"
            />
          </div>

          {/* Company */}
          <div className="space-y-2">
            <Label htmlFor="company" className="text-sm font-semibold text-slate-700">Company *</Label>
            <Input
              id="company"
              placeholder="Shopify"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              required
              maxLength={100}
              className="rounded-xl border-2 border-slate-200 focus:border-[#4669ed] focus:ring-0"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate" className="text-sm font-semibold text-slate-700">Start Date *</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
                className="rounded-xl border-2 border-slate-200 focus:border-[#4669ed] focus:ring-0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate" className="text-sm font-semibold text-slate-700">
                End Date {!isCurrent && '(Optional)'}
              </Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={isCurrent}
                max={new Date().toISOString().split('T')[0]}
                className="rounded-xl border-2 border-slate-200 focus:border-[#4669ed] focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          {/* Current Job Checkbox */}
          <div className="flex items-center space-x-3">
            <Checkbox
              id="isCurrent"
              checked={isCurrent}
              onCheckedChange={(checked) => {
                setIsCurrent(checked === true);
                if (checked) {
                  setEndDate('');
                }
              }}
            />
            <Label htmlFor="isCurrent" className="text-sm font-normal cursor-pointer text-slate-700">
              I currently work here
            </Label>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location" className="text-sm font-semibold text-slate-700">Location (Optional)</Label>
            <Input
              id="location"
              placeholder="San Francisco, CA"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              maxLength={100}
              className="rounded-xl border-2 border-slate-200 focus:border-[#4669ed] focus:ring-0"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-semibold text-slate-700">Description (Optional)</Label>
            <Textarea
              id="description"
              placeholder="Led ML personalization team building real-time recommendation systems processing 10M+ predictions daily.&#10;Increased conversion by 62% through collaborative filtering and context-aware models."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={6}
              maxLength={2000}
              className="rounded-xl border-2 border-slate-200 focus:border-[#4669ed] focus:ring-0 resize-none"
            />
            <p className="text-xs text-slate-500 mt-1">
              Describe your responsibilities and achievements. You can use bullet points (•) or line breaks.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
              className="rounded-[48px] border-2 border-slate-200"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-[#4669ed] hover:bg-[#2b42a3] rounded-[48px] text-white"
            >
              {isLoading ? 'Saving...' : isEditing ? 'Update Experience' : 'Add Experience'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

